/* CSBH cart drawer: intelligent 2-card upsells + silent free-shipping code apply.
   Pairing map lives in assets/csbh-pairings.json (category -> A/B arms of 2 handles).
   Arm is deterministic per cart token, so exposures are clean for attach-rate
   measurement: exposure -> GA event csbh_upsell_view + _csbh_up cart attribute
   (lands on the order's note_attributes); add -> csbh_upsell_add.
   Runs outside the drawer's re-rendered HTML (innerHTML swaps kill inline
   scripts), re-triggered by a MutationObserver on <cart-drawer>. */
(function () {
  'use strict';
  /* % of carts that see NO upsells (control group for the upsells-vs-none test).
     Deterministic per cart token; holdout carts stamp cat:'_holdout' so converted
     orders split cleanly into exposed vs control. 0 = everyone sees upsells. */
  var HOLDOUT_PCT = 25;
  var lastKey = null;
  var building = false;
  var pairings = null;
  var pairingsUrl = null;
  var lastStamp = null;

  function fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── keep scroll put when using the +/- stepper ───────────────────────────
     Dawn swaps the whole #CartDrawer innerHTML on every qty change (its native
     stepper does this inline in updateQuantity — no single method to wrap),
     rebuilding the scroll container and snapping it to the top ("cart jumps to
     top" on a stepper click, reported 2026-07-09). We snapshot the scroll
     position ONLY when a stepper button is pressed, then restore it on the
     ensuing re-render. Scoping to the button press means opens and adds are
     never touched — those correctly start at the top. */
  var pendingScroll = null;
  var pendingUntil = 0;
  function drawerScroller() {
    var items = document.querySelector('#CartDrawer cart-drawer-items');
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    if (items && items.scrollHeight > items.clientHeight + 4) return items;
    if (inner && inner.scrollHeight > inner.clientHeight + 4) return inner;
    return items || inner;
  }
  function armScrollRestore() {
    var s = drawerScroller();
    pendingScroll = s ? s.scrollTop : null;
    pendingUntil = new Date().getTime() + 1200; /* only for this re-render burst */
  }
  function restoreScroll() {
    if (pendingScroll == null || new Date().getTime() > pendingUntil) return;
    var y = pendingScroll;
    var s = drawerScroller();
    if (s) s.scrollTop = y;
    requestAnimationFrame(function () { var s2 = drawerScroller(); if (s2) s2.scrollTop = y; });
  }
  /* snapshot on any stepper button press inside the drawer (unit + by-weight) */
  document.addEventListener('pointerdown', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('#CartDrawer .quantity__button');
    if (btn) armScrollRestore();
  }, true);

  function byWeight(p) {
    /* the 'sell-by-weight' tag only (Measura cutover 10/28): the Clingy cart transform reprices
       tagged products only, so an untagged -lbs product would charge the per-lb price for half a pound */
    return !!(p.tags && /\bsell-by-weight\b/i.test(String(p.tags)));
  }

  /* portion rule: never more than 1/2 lb of meat; other by-weight items 1 lb */
  function lbQty(type) {
    return /charcuterie|salami|prosciutto|meat|cured/i.test(type || '') ? 0.5 : 1;
  }

  /* one-per-family rule (Dave 2026-07-08): cheeses and cured meats can always
     be suggested, but pantry families (honey, chips, crackers...) get upsold
     at most once — nobody wants a second honey. Order matters: potato chips
     before generic "crisps" (Raincoast/34° crisps are crackers). */
  function famKey(title, type) {
    if (/cheese|dairy/i.test(type || '')) return null;
    if (/cured meat|charcuterie/i.test(type || '')) return null;
    var t = (title || '').toLowerCase();
    if (/honey/.test(t)) return 'honey';
    if (/pesto|tapenade/.test(t)) return 'pesto';
    if (/potato|vinegar chip|\bchips\b/.test(t)) return 'chips';
    if (/crisp|cracker|flatbread|crostini|biscuit|toast/.test(t)) return 'crackers';
    if (/\bdates?\b/.test(t)) return 'dates';
    if (/quince|membrillo|\bjam\b|preserve|marmalade|mostarda|compote|confit/.test(t)) return 'jam';
    if (/almond|walnut|pistachio|cashew|pecan|\bnuts?\b/.test(t)) return 'nuts';
    if (/chocolate/.test(t)) return 'chocolate';
    if (/olive/.test(t)) return 'olives';
    return null;
  }

  function cartItemFor(p) {
    /* Measura's model: quantity stays 1, the TOTAL weight lives in the props.
       (multi-unit schemes get silently consolidated by Measura on the cart
       page, leaving stale qty UIs that then multiply the weight — real
       overcharge bug, found 2026-07-07) */
    var item = { id: p.variantId, quantity: 1 };
    if (byWeight(p)) {
      var q = lbQty(p.type);
      var up = '$' + (p.priceCents / 100).toFixed(2) + ' / lb';
      item.properties = {
        'Unit price': up,
        'Net weight': q + ' lb',
        _clingy_weight: String(q),
        _clingy_unit: 'lb',
        _clingy_sbw: '1'
      };
    }
    return item;
  }

  function priceLabel(p) {
    if (byWeight(p)) {
      var q = lbQty(p.type);
      var lbl = q === 0.5 ? '1/2 lb' : q + ' lb';
      return lbl + ' @ ' + fmtMoney(p.priceCents) + '/lb = ' + fmtMoney(p.priceCents * q);
    }
    return fmtMoney(p.priceCents);
  }

  function normalizeFromProductJs(p) {
    /* upsell eligibility rules (Dave 2026-07-07):
       single-variant only (binary add), never subscription products */
    if (p.requires_selling_plan) return null;
    if ((p.variants || []).length !== 1) return null;
    var v = p.variants[0];
    if (!v.available) return null;
    return {
      handle: p.handle,
      title: p.title,
      type: p.type || '',
      tags: p.tags || [],
      priceCents: v.price,
      variantId: v.id,
      image: p.featured_image || (p.images && p.images[0]) || ''
    };
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  function imgUrl(src, w) {
    if (!src) return '';
    return src.replace(/(\.[a-z]{3,4})(\?|$)/i, '_' + w + 'x$1$2');
  }

  function cleanTitle(t) {
    return t
      .replace(/\s*\([^)]*\)\s*$/, '')
      .replace(/\s*[-–]\s*lbs?\.?$/i, '')
      .replace(/\s+lbs?\.?$/i, '');
  }

  /* title root for same-cheese-different-format exclusion:
     "Pont L'Eveque - 3lbPiece" and "Pont L'Eveque" must collide */
  function titleRoot(t) {
    return (t || '')
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/[-–].*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function hashStr(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) {
      h = (h * 31 + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
  }

  function gaPub(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* analytics must never break the cart */ }
  }

  function getPairings() {
    if (pairings) return Promise.resolve(pairings);
    if (!pairingsUrl) return Promise.resolve(null);
    return fetchJson(pairingsUrl).then(function (j) {
      pairings = j;
      return j;
    }).catch(function () { return null; });
  }

  /* anchor = first cheese category in cart; cured-meat-only carts -> 'charcuterie' */
  function anchorKey(box, map) {
    var cats = (box.getAttribute('data-cats') || '').split('|');
    for (var i = 0; i < cats.length; i++) {
      var c = cats[i].trim();
      if (c && map[c]) return c;
    }
    var types = (box.getAttribute('data-types') || '').split('|');
    for (var j = 0; j < types.length; j++) {
      if (/cured meat/i.test(types[j])) return 'charcuterie';
    }
    return '_default';
  }

  /* stamp exposure on the cart so it lands on the order (attach-rate numerator link) */
  function stampExposure(payload) {
    var s = JSON.stringify(payload);
    if (s === lastStamp) return;
    lastStamp = s;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _csbh_up: s } })
    }).catch(function () {});
  }

  function render(row, picks) {
    row.innerHTML = '';
    picks.forEach(function (p) {
      var card = document.createElement('div');
      card.className = 'csbh-upsell-card';
      var imgLink = document.createElement('a');
      imgLink.className = 'csbh-upsell-card__imglink';
      imgLink.href = '/products/' + p.handle;
      imgLink.setAttribute('aria-label', p.title);
      var img = document.createElement('img');
      img.className = 'csbh-upsell-card__img';
      img.loading = 'lazy';
      img.alt = p.title;
      img.src = imgUrl(p.image, 400);
      imgLink.appendChild(img);
      var mid = document.createElement('div');
      mid.className = 'csbh-upsell-card__body';
      var title = document.createElement('p');
      title.className = 'csbh-upsell-card__title';
      title.textContent = cleanTitle(p.title);
      mid.appendChild(title);
      if (p.why) {
        var why = document.createElement('p');
        why.className = 'csbh-upsell-card__why';
        why.textContent = p.why;
        mid.appendChild(why);
      }
      var price = document.createElement('p');
      price.className = 'csbh-upsell-card__price';
      price.textContent = priceLabel(p);
      mid.appendChild(price);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'csbh-upsell-card__add';
      btn.textContent = 'Add';
      btn.addEventListener('click', function () {
        btn.disabled = true;
        btn.textContent = 'Adding…';
        gaPub('csbh_upsell_add', { handle: p.handle, exp: p.exp || null });
        addToCart(p).catch(function () {
          btn.disabled = false;
          btn.textContent = 'Add';
        });
      });
      card.appendChild(imgLink);
      card.appendChild(mid);
      card.appendChild(btn);
      row.appendChild(card);
    });
  }

  function addToCart(p) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [cartItemFor(p)],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed ' + r.status);
        return r.json();
      })
      .then(function (state) {
        lastKey = null; /* force rebuild against the new cart */
        var drawer = document.querySelector('cart-drawer');
        if (drawer && state.sections) drawer.renderContents(state);
      });
  }

  function build() {
    var box = document.getElementById('csbh-upsells');
    if (!box || building) return;
    pairingsUrl = box.getAttribute('data-pairings-url') || pairingsUrl;
    var inCart = (box.getAttribute('data-in-cart') || '').split(',').filter(Boolean);
    var key = inCart.join(',');
    /* same cart AND the cards are still in the DOM: nothing to do. If a drawer
       re-render wiped the row (same key), fall through and rebuild — otherwise
       upsells vanish on reopen (reported 2026-07-08) */
    if (key === lastKey && document.getElementById('csbh-upsells-row').children.length) {
      box.hidden = false;
      return;
    }
    building = true;
    var firstId = box.getAttribute('data-first-id');
    var seen = {};
    inCart.forEach(function (h) { seen[h] = true; });
    var seenRoots = {};
    var cartTitles = (box.getAttribute('data-in-cart-titles') || '').split('|');
    var cartTypes = (box.getAttribute('data-types') || '').split('|');
    var cartFams = {};
    cartTitles.filter(Boolean).forEach(function (t, i) {
      seenRoots[titleRoot(t)] = true;
      var f = famKey(t, cartTypes[i] || '');
      if (f) cartFams[f] = true;
    });
    function isDupe(p) {
      var root = titleRoot(p.title);
      if (seen[p.handle] || seenRoots[root]) return true;
      var f = famKey(p.title, p.type);
      if (f && cartFams[f]) return true; /* family already in cart: suggest something else */
      seen[p.handle] = true;
      seenRoots[root] = true;
      if (f) cartFams[f] = true; /* and never two of a family side by side */
      return false;
    }

    var exp = { v: null, cat: null, arm: null };

    Promise.all([getPairings(), fetchJson('/cart.js').catch(function () { return {}; })])
      .then(function (res) {
        var map = res[0] && res[0].map;
        var token = (res[1] && res[1].token) || 'anon';
        if (HOLDOUT_PCT > 0 && hashStr(token + '|holdout') % 100 < HOLDOUT_PCT) {
          exp.v = (res[0] && res[0].version) || null;
          exp.cat = '_holdout';
          exp.arm = -1;
          return null; /* control cart: no upsells, but exposure still stamped */
        }
        if (!map) return [];
        exp.v = res[0].version;
        exp.cat = anchorKey(box, map);
        var arms = map[exp.cat] || map._default || [];
        if (!arms.length) return [];
        exp.arm = hashStr(token + '|' + exp.v + '|' + exp.cat) % arms.length;
        var entries = arms[exp.arm]
          .map(function (e) { return typeof e === 'string' ? { h: e, why: '' } : e; })
          .filter(function (e) { return e && e.h && !seen[e.h]; });
        return Promise.all(entries.map(function (e) {
          return fetchJson('/products/' + e.h + '.js')
            .then(normalizeFromProductJs)
            .then(function (n) {
              if (n) n.why = e.why || '';
              return n;
            })
            .catch(function () { return null; });
        }));
      })
      .then(function (mapPicks) {
        if (mapPicks === null) return null; /* holdout cart */
        var picks = (mapPicks || []).filter(Boolean).filter(function (p) {
          return !isDupe(p);
        });
        if (picks.length >= 2 || !firstId) return picks;
        var excluded = (pairings && pairings.exclude) || [];
        /* top-up only when a mapped item is unavailable */
        return fetchJson('/recommendations/products.json?product_id=' + firstId + '&limit=8&intent=related')
          .then(function (data) {
            (data.products || []).forEach(function (rp) {
              if (picks.length >= 2) return;
              if (!rp.available) return;
              if (/gift card/i.test(rp.type || '')) return;
              if (/dom society/i.test(rp.title || '')) return;
              if (excluded.indexOf(rp.handle) !== -1) return; /* bundles etc. */
              var n = normalizeFromProductJs(rp);
              if (!n || isDupe(n)) return;
              picks.push(n);
            });
            return picks;
          })
          .catch(function () { return picks; });
      })
      .then(function (picks) {
        var row = document.getElementById('csbh-upsells-row');
        if (!row) return;
        if (picks === null) { /* holdout: hide, but record the exposure */
          row.innerHTML = '';
          box.hidden = true;
          gaPub('csbh_upsell_view', { v: exp.v, cat: '_holdout', arm: -1, shown: '' });
          stampExposure({ v: exp.v, cat: '_holdout', arm: -1, shown: [] });
          lastKey = key;
          return;
        }
        picks = picks.slice(0, 2);
        if (picks.length) {
          var shown = picks.map(function (p) { return p.handle; });
          picks.forEach(function (p) { p.exp = { v: exp.v, cat: exp.cat, arm: exp.arm }; });
          render(row, picks);
          box.hidden = false;
          gaPub('csbh_upsell_view', { v: exp.v, cat: exp.cat, arm: exp.arm, shown: shown.join(',') });
          stampExposure({ v: exp.v, cat: exp.cat, arm: exp.arm, shown: shown });
        } else {
          box.hidden = true;
        }
        lastKey = key;
      })
      .finally(function () {
        building = false;
      });
  }

  /* silently apply the segment-restricted free-shipping code for qualified
     accounts; checkout enforces eligibility server-side. Re-applied on every
     page load and drawer change — Shopify drops the applied code when the
     cart is swapped (login/logout) or a checkout completes, so a
     once-per-session latch goes stale and the code never comes back. */
  var applyingFreeShip = false;
  function applyFreeShip() {
    var bar = document.getElementById('csbh-shipbar');
    if (!bar) return;
    var state = bar.getAttribute('data-fs');
    var code = bar.getAttribute('data-code');
    if (!code || (state !== 'member' && state !== 'unlocked')) return;
    if (applyingFreeShip) return;
    applyingFreeShip = true;
    fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .catch(function () {})
      .finally(function () { applyingFreeShip = false; });
  }

  /* ── by-weight stepper: +/- moves the WEIGHT by 1/2 lb (qty pinned at 1) ── */
  var stepping = false;
  function stepWeight(lineIndex, delta) {
    if (stepping) return;
    stepping = true;
    fetchJson('/cart.js')
      .then(function (c) {
        var item = c.items[lineIndex - 1];
        if (!item || !item.properties || !item.properties['Net weight']) throw new Error('no weight line');
        /* the line's own unit (our stamp, else the Net weight text) and the PDP picker's step */
        var net = String(item.properties['Net weight']);
        var unit = item.properties._clingy_unit || (/\boz\b/i.test(net) ? 'oz' : /\bg\b/i.test(net) ? 'g' : 'lb');
        var step = unit === 'g' ? 5 : unit === 'oz' ? 1 : 0.5;
        var unitW = parseFloat(net);
        var totalW = unitW * item.quantity;
        var newW = Math.max(step, Math.round((totalW + (delta > 0 ? step : -step)) / step) * step);
        newW = Math.round(newW * 100) / 100;
        if (newW === totalW) throw new Error('at minimum');
        var props = {};
        Object.keys(item.properties).forEach(function (k) { props[k] = item.properties[k]; });
        props['Net weight'] = newW + ' ' + unit;
        /* the Clingy cart transform prices from _clingy_weight: it moves with the weight shown */
        if (props._clingy_weight != null) props._clingy_weight = String(newW);
        try {
          var md = JSON.parse(props._measura_data);
          if (md.m && md.m.d && md.m.d[0]) md.m.d[0].v = newW;
          if (md.measurement && md.measurement.dimensions && md.measurement.dimensions[0]) {
            md.measurement.dimensions[0].value = newW;
          }
          if (md.um && md.um.v === totalW) md.um.v = newW;
          props._measura_data = JSON.stringify(md);
        } catch (e) { /* leave _measura_data untouched if unparseable */ }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 1,
            properties: props,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });
      })
      .then(function (r) { return r && r.json ? r.json() : null; })
      .then(function (state) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && state && state.sections) {
          drawer.getSectionsToRender().forEach(function (section) {
            var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (el && state.sections[section.id]) {
              el.innerHTML = drawer.getSectionInnerHTML(state.sections[section.id], section.selector);
            }
          });
        }
      })
      .catch(function () {})
      .finally(function () { stepping = false; });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.csbh-qty--weight .quantity__button');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var input = btn.closest('quantity-input').querySelector('.quantity__input');
    var lineIndex = parseInt(input.getAttribute('data-index'), 10);
    if (!lineIndex) return;
    stepWeight(lineIndex, btn.getAttribute('name') === 'plus' ? 0.5 : -0.5);
  }, true);

  /* backstop: Dawn's product-form is supposed to open the drawer after an
     add, but after a close/back-out the open sometimes doesn't re-fire.
     If the drawer still isn't active shortly after an ATC click, open it. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest &&
      e.target.closest('.product-form__submit, .csbh-sticky-cta');
    if (!btn) return;
    setTimeout(function () {
      var d = document.querySelector('cart-drawer');
      if (!d || d.classList.contains('active') || d.classList.contains('is-empty')) return;
      if (typeof d.open === 'function') {
        d.open();
      } else {
        d.classList.add('animate', 'active');
      }
    }, 1000);
  });

  /* Dawn's quantity-input disables the minus button when value <= min;
     weight lines pin qty at 1, so force it back on after every render */
  function enableWeightSteppers() {
    document.querySelectorAll('.csbh-qty--weight .quantity__button[name="minus"]').forEach(function (b) {
      b.disabled = false;
      b.removeAttribute('disabled');
    });
  }

  /* ── sold-out notice (Dave 11/2) ──────────────────────────────────────────
     Shopify zeroes a cart line whose item sells out (MERCHANDISE_OUT_OF_STOCK) on
     the shopper's next cart change, and the line just vanished. Remember the
     cart per variant (a weight step changes the line key, not the variant); when
     a variant's quantity drops and the shopper pressed no remove / minus / qty
     box, ask the product whether it sold out and say so in the drawer. */
  var SNAP = 'csbhCartSnap', NOTE = 'csbhCartNote';
  var userTouch = 0, checking = false;
  function store(k, v) { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function stored(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  ['click', 'change'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var t = e.target && e.target.closest && e.target.closest('cart-remove-button, cart-drawer quantity-input, cart-items quantity-input, [href*="/cart/change"]');
      if (t) userTouch = new Date().getTime();
    }, true);
  });
  function showNote() {
    var note = stored(NOTE);
    var old = document.getElementById('csbh-cart-note');
    if (!note || new Date().getTime() > note.until) { if (old) old.remove(); if (note) store(NOTE, null); return; }
    var items = document.querySelector('#CartDrawer cart-drawer-items');
    if (!items || old) return;
    var div = document.createElement('div');
    div.id = 'csbh-cart-note';
    div.setAttribute('role', 'status');
    div.style.cssText = 'display:flex;gap:12px;align-items:flex-start;margin:0 0 12px;padding:12px 14px;background:#fdf6e3;border-left:3px solid #8b1e3f;font-size:14px;line-height:1.4';
    var p = document.createElement('p');
    p.style.cssText = 'margin:0;flex:1';
    p.textContent = note.text;
    var x = document.createElement('button');
    x.type = 'button';
    x.setAttribute('aria-label', 'Dismiss');
    x.textContent = '×';
    x.style.cssText = 'background:none;border:0;font-size:20px;line-height:1;cursor:pointer;padding:0 2px';
    x.addEventListener('click', function () { store(NOTE, null); div.remove(); });
    div.appendChild(p);
    div.appendChild(x);
    items.insertBefore(div, items.firstChild);
  }
  function checkDropped() {
    if (checking) return;
    checking = true;
    var touched = new Date().getTime() - userTouch < 15000;
    fetchJson('/cart.js')
      .then(function (c) {
        var now = { token: c.token, v: {} };
        (c.items || []).forEach(function (i) {
          var r = now.v[i.variant_id] || (now.v[i.variant_id] = { q: 0, h: i.handle, t: i.product_title });
          r.q += i.quantity;
        });
        var before = stored(SNAP);
        store(SNAP, now);
        if (touched || !before || before.token !== c.token) return;
        var dropped = Object.keys(before.v).filter(function (id) { return (now.v[id] ? now.v[id].q : 0) < before.v[id].q; });
        return Promise.all(dropped.map(function (id) {
          var was = before.v[id];
          return fetchJson('/products/' + was.h + '.js').then(function (p) {
            var v = (p.variants || []).filter(function (x) { return String(x.id) === id; })[0];
            if (v && !v.available) return was.t + ' just sold out, so it is no longer in your cart.';
            var left = now.v[id] ? now.v[id].q : 0;
            if (v && left > 0) return 'We only have ' + left + ' of ' + was.t + ' left, so your cart now has ' + left + '.';
            return null; /* still for sale: removed some other way (another tab), say nothing */
          }).catch(function () { return null; });
        })).then(function (msgs) {
          msgs = msgs.filter(Boolean);
          if (!msgs.length) return;
          store(NOTE, { text: msgs.join(' '), until: new Date().getTime() + 10 * 60 * 1000 });
          var old = document.getElementById('csbh-cart-note');
          if (old) old.remove();
        });
      })
      .catch(function () {})
      .then(function () { checking = false; showNote(); });
  }

  var debounceTimer = null;
  function onMutate() {
    restoreScroll(); /* immediate, before the debounced rebuild, to avoid flicker */
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      build();
      applyFreeShip();
      enableWeightSteppers();
      checkDropped();
    }, 150);
  }

  function init() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(onMutate).observe(drawer, { childList: true, subtree: true });
    build();
    applyFreeShip();
    enableWeightSteppers();
    checkDropped();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
